import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Target,
  Brain,
  Calendar,
  TrendingUp,
  Activity,
  Zap,
  ChevronRight,
  Star,
  Award,
  BarChart3,
  CheckCircle2,
  AlertTriangle
} from 'lucide-react';
import api from '../lib/axios';
import { toast } from 'sonner';

function IkonsPlayerDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/ikons/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'green': return 'bg-green-500';
      case 'yellow': return 'bg-yellow-500';
      case 'red': return 'bg-red-500';
      default: return 'bg-gray-400';
    }
  };

  const getStatusBg = (status) => {
    switch (status) {
      case 'green': return 'bg-green-50 border-green-200';
      case 'yellow': return 'bg-yellow-50 border-yellow-200';
      case 'red': return 'bg-red-50 border-red-200';
      default: return 'bg-gray-50 border-gray-200';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'green': return 'Óptimo';
      case 'yellow': return 'Ajuste Necesario';
      case 'red': return 'Alerta';
      default: return 'Sin datos';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto" data-testid="ikons-player-dashboard">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-amber-100 rounded-lg">
              <Star className="h-8 w-8 text-amber-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-[#4F4F4F]">
                Ikons Player System
              </h1>
              <p className="text-[#808080]">Tu sistema de desarrollo profesional</p>
            </div>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Mapa 360 Card */}
          <Card className={`border-2 ${getStatusBg(dashboardData?.latest_map_360?.status)}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity className="h-5 w-5 text-amber-600" />
                Mapa de Rendimiento 360°
              </CardTitle>
              <CardDescription>Tu estado actual</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboardData?.latest_map_360 ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-4xl font-bold">{dashboardData.latest_map_360.total_score}</span>
                    <span className="text-2xl text-muted-foreground">/25</span>
                  </div>
                  <Progress 
                    value={(dashboardData.latest_map_360.total_score / 25) * 100} 
                    className="h-3"
                  />
                  <Badge className={`${getStatusColor(dashboardData.latest_map_360.status)} text-white`}>
                    {getStatusLabel(dashboardData.latest_map_360.status)}
                  </Badge>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Sin registros aún</p>
              )}
            </CardContent>
          </Card>

          {/* Player Score Card */}
          <Card className={`border-2 ${getStatusBg(dashboardData?.latest_score?.status)}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-amber-600" />
                Ikons Player Score
              </CardTitle>
              <CardDescription>Score semanal</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboardData?.latest_score ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-4xl font-bold">{dashboardData.latest_score.total_score}</span>
                    <span className="text-2xl text-muted-foreground">/25</span>
                  </div>
                  <Progress 
                    value={(dashboardData.latest_score.total_score / 25) * 100} 
                    className="h-3"
                  />
                  <Badge className={`${getStatusColor(dashboardData.latest_score.status)} text-white`}>
                    {getStatusLabel(dashboardData.latest_score.status)}
                  </Badge>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Sin registros aún</p>
              )}
            </CardContent>
          </Card>

          {/* Weekly Focus Card */}
          <Card className="border-2 border-amber-200 bg-amber-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-amber-600" />
                Foco Semanal
              </CardTitle>
              <CardDescription>Tu objetivo esta semana</CardDescription>
            </CardHeader>
            <CardContent>
              {dashboardData?.current_weekly_plan?.weekly_focus ? (
                <p className="font-medium text-[#4F4F4F]">
                  {dashboardData.current_weekly_plan.weekly_focus}
                </p>
              ) : (
                <p className="text-muted-foreground text-sm">Define tu foco semanal</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Actions */}
        <h2 className="text-xl font-semibold mb-4 text-[#4F4F4F]">Acciones Rápidas</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {/* Pre-partido */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-white"
            onClick={() => navigate('/ikons/pre-match')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Zap className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Pre-Partido</h3>
                    <p className="text-sm text-muted-foreground">Prepararme para el partido</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-blue-400" />
              </div>
            </CardContent>
          </Card>

          {/* Post-partido */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white"
            onClick={() => navigate('/ikons/post-match')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-orange-100 rounded-xl">
                    <Brain className="h-6 w-6 text-orange-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Post-Partido</h3>
                    <p className="text-sm text-muted-foreground">Cerrar el partido</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-orange-400" />
              </div>
            </CardContent>
          </Card>

          {/* Semana */}
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow border-2 border-green-200 bg-gradient-to-br from-green-50 to-white"
            onClick={() => navigate('/ikons/week')}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <Calendar className="h-6 w-6 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">Plan Semanal</h3>
                    <p className="text-sm text-muted-foreground">Mi semana de desarrollo</p>
                  </div>
                </div>
                <ChevronRight className="h-5 w-5 text-green-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Score History */}
        {dashboardData?.score_history && dashboardData.score_history.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-amber-600" />
                Tu Evolución
              </CardTitle>
              <CardDescription>Historial de Player Scores</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-2 h-32">
                {dashboardData.score_history.slice().reverse().map((score, index) => (
                  <div key={index} className="flex-1 flex flex-col items-center gap-1">
                    <div 
                      className={`w-full rounded-t-lg ${getStatusColor(score.status)}`}
                      style={{ height: `${(score.total_score / 25) * 100}%` }}
                    />
                    <span className="text-xs font-medium">{score.total_score}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Daily Standard Progress */}
        {dashboardData?.current_daily_standard && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-amber-600" />
                Estándar Diario
              </CardTitle>
              <CardDescription>¿Estás manteniendo tu nivel?</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-7 gap-2">
                {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((day, index) => {
                  const dayData = dashboardData.current_daily_standard.days?.[index];
                  const completed = dayData?.trained_professionally && dayData?.competed;
                  return (
                    <div 
                      key={day}
                      className={`aspect-square rounded-lg flex items-center justify-center font-medium ${
                        completed ? 'bg-green-100 text-green-700' : 
                        dayData ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}

export default IkonsPlayerDashboard;
